import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { css } from 'twin.macro';
import ReactSelect from 'react-select';
import DateTimePicker from 'react-datetime-picker/dist/DateTimePicker';

import { AdminTable } from '../../reusableStyles';
import LoadingSpiner from '../../../../_elements/LoadingSpiner';
import Pagination from '../../Pagination';

import {
    fetchWrapper,
    getChannelIdFromSettingsURL,
    getDuration,
    getTeamIdFromURL,
    isAdminPanel,
    toastWrapper,
} from '../../../../_helpers';
import SortedTableHeaderIcon from '../../SortedTableHeaderIcon';
import TeamSelectFilter from '../../TeamSelectFilter';
import ChannelSelectFilter from '../../ChannelSelectFilter';
import { GoToChannel, GoToTeamSettings } from '@/_elements/Links';
import dayjs from 'dayjs';
import AvatarStack from '@/_elements/AvatarStack/AvatarStack';
import Skeleton from 'react-loading-skeleton';
import ConferenceRow from './ConferenceRow';

export const StatusSelect = ({ value, onChange }) => (
    <ReactSelect
        value={value ? { value: value, label: value } : null}
        onChange={(option) => onChange(option ? option.value : '')}
        options={[
            { label: 'Completed', value: 'completed' },
            { label: 'Ongoing', value: 'ongoing' },
            { label: 'Created', value: 'created' },
        ]}
        placeholder="Status: All"
        isClearable
    />
);
export const RecordingStatusSelect = ({ value, onChange }) => (
    <ReactSelect
        value={value ? { value: value, label: value } : null}
        onChange={(option) => onChange(option ? option.value : '')}
        options={[
            { label: 'Recorded', value: 'recorded' },
            { label: 'Not Recorded', value: 'notrecorded' },
        ]}
        placeholder="Recording: All"
        isClearable
    />
);

const Conference = () => {
    const isAdminView = isAdminPanel();
    const teamId = getTeamIdFromURL();
    const channelId = getChannelIdFromSettingsURL();
    const [items, setItems] = useState({ results: [], page: 1, totalResults: 0, limit: 10 });
    const [isLoading, setIsLoading] = useState(false);
    const [totalUsage, setTotalUsage] = useState({});

    const currentDate = new Date();

    const [query, setQuery] = useState({
        status: '',
        recordingStatus: '',
        team: { label: 'Select a Team', value: '' },
        room: { label: 'Select a Room', value: '' },
        page: 1,
        sortBy: 'createdAt:desc',
        from: new Date(currentDate.getFullYear(), 0, 1).toISOString(),
        to: '',
    });

    const getItems = async () => {
        try {
            setIsLoading(true);
            const response = await fetchWrapper.get(
                `conference?limit=10&page=${query.page}${
                    query.team?.value || teamId ? `&team=${query.team.value || teamId}` : ''
                }${query.room?.value || channelId ? `&room=${query.room.value || channelId}` : ''}${
                    query.status ? `&status=${query.status}` : ''
                }${query.from ? `&startDate=${query.from}` : ''}${query.to ? `&endDate=${query.to}` : ''}&sortBy=${query.sortBy}${
                    query.recordingStatus ? `&recordingStatus=${query.recordingStatus}` : ''
                }`
            );
            setItems(response);
        } catch (err) {
            if (err?.name === 'AbortError') return;
            toastWrapper.error(err.message || err);
        } finally {
            setIsLoading(false);
        }
    };

    const getTotalUsage = async () => {
        try {
            const response = await fetchWrapper.get(
                `conference/total-usage?limit=10&page=${query.page}${
                    query.team?.value || teamId ? `&team=${query.team.value || teamId}` : ''
                }${query.room?.value || channelId ? `&room=${query.room.value || channelId}` : ''}${
                    query.status ? `&status=${query.status}` : ''
                }${query.from ? `&startDate=${query.from}` : ''}${query.to ? `&endDate=${query.to}` : ''}&sortBy=${query.sortBy}${
                    query.recordingStatus ? `&recordingStatus=${query.recordingStatus}` : ''
                }`
            );
            setTotalUsage(response);
        } catch (err) {
            if (err?.name === 'AbortError') return;
            toastWrapper.warn(err.message || err);
        }
    };

    const handleSort = (key) => {
        const currentSort = query.sortBy.split(':');
        const currentKey = currentSort[0];
        const currentDirection = currentSort[1];

        let newDirection = 'desc';
        if (currentKey === key) {
            newDirection = currentDirection === 'asc' ? 'desc' : 'asc';
        }

        setQuery((prev) => ({
            ...prev,
            sortBy: `${key}:${newDirection}`,
            page: 1,
        }));
    };

    const onPageChanged = (data) => {
        if (data.currentPage !== query.page && data.currentPage > 0) {
            setQuery((prev) => ({ ...prev, page: data.currentPage }));
        }
    };

    const handleFilterChange = (filterType, option) => {
        setQuery((prev) => {
            const newQuery = {
                ...prev,
                [filterType]: option,
                page: 1,
            };

            if (filterType === 'team' && !option?.value) {
                newQuery.room = { label: 'Select a Room', value: '' };
            }

            return newQuery;
        });
    };

    const handleStatusFilterChange = (status) => {
        setQuery((prev) => ({
            ...prev,
            status,
            page: 1,
        }));
    };

    const handleRecordingStatusFilterChange = (recordingStatus) => {
        setQuery((prev) => ({
            ...prev,
            recordingStatus,
            page: 1,
        }));
    };

    const handleQueryChange = (updates) => {
        setQuery((prev) => ({
            ...prev,
            ...updates,
            page: 1,
        }));
    };

    // Fetch data when query changes
    useEffect(() => {
        getItems();
        getTotalUsage();
    }, [query]);

    const getSortConfig = () => {
        const [key, direction] = query.sortBy.split(':');
        return { key, direction };
    };

    const sortConfig = getSortConfig();

    const onUpdateItem = (item) => {
        setItems({
            ...items,
            results: (items?.results || []).map((_item) => (_item?._id === item?._id ? { ..._item, ...item } : _item)),
        });
    };

    return (
        <div>
            {/* Total Usage Section */}
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                    padding: 12px;
                    align-items: center;
                    margin-bottom: 24px;
                `}
            >
                <div
                    css={css`
                        background: white;
                        padding: 6px 12px;
                        border-radius: 12px;
                        display: flex;
                        gap: 12px;
                        align-items: center;
                        flex-wrap: wrap;
                        justify-content: center;
                    `}
                >
                    <span className="text-primary" style={{ fontWeight: 'bold' }}>
                        Total Usage Data:
                    </span>
                    {isLoading ? (
                        <Skeleton width={500} height={30} />
                    ) : (
                        <>
                            <span className="badge badge-secondary px-4 py-2" style={{ fontSize: '14px' }}>
                                Total Conferences: {totalUsage.totalConferences || 'N/A'}
                            </span>
                            <span className="badge badge-danger px-4 py-2" style={{ fontSize: '14px' }}>
                                Total Duration:{' '}
                                {totalUsage.totalDurationMinutes
                                    ? (() => {
                                          const totalMinutes = Math.round(totalUsage.totalDurationMinutes);
                                          const days = Math.floor(totalMinutes / (60 * 24));
                                          const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
                                          const minutes = totalMinutes % 60;

                                          const parts = [];
                                          if (days > 0) parts.push(`${days}d`);
                                          if (hours > 0) parts.push(`${hours}h`);
                                          if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

                                          return parts.join(' ');
                                      })()
                                    : '0m'}
                            </span>
                            <span className="badge badge-success px-4 py-2" style={{ fontSize: '14px' }}>
                                Average Duration:{' '}
                                {totalUsage.averageDurationMinutes
                                    ? (() => {
                                          const totalMinutes = totalUsage.averageDurationMinutes;
                                          const hours = Math.floor(totalMinutes / 60);
                                          const minutes = Math.round(totalMinutes % 60);

                                          if (hours > 0) {
                                              return `${hours}h ${minutes}m`;
                                          } else {
                                              return `${minutes}m`;
                                          }
                                      })()
                                    : '0m'}
                            </span>
                        </>
                    )}
                </div>

                {/* Date Range Filters */}
                <div
                    className="d-flex flex-column"
                    css={css`
                        gap: 12px;
                        width: 100%;
                        max-width: 600px;
                    `}
                >
                    <div
                        style={{
                            position: 'relative',
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <span>From </span>
                        <DateTimePicker
                            css={css`
                                display: inline-block;
                                width: 50%;
                                background: ${isLoading ? 'transparent' : 'white'};
                            `}
                            onChange={(value) => handleQueryChange({ from: value ? value.toISOString() : '' })}
                            maxDate={query.to ? new Date(query.to) : new Date()}
                            value={query.from ? new Date(query.from) : null}
                            format="MM-dd-y hh:mm a"
                            calendarType={'US'}
                            disabled={isLoading}
                        />
                        <span>To </span>
                        <DateTimePicker
                            css={css`
                                display: inline-block;
                                width: 50%;
                                background: ${isLoading ? 'transparent' : 'white'};
                            `}
                            onChange={(value) => handleQueryChange({ to: value ? value.toISOString() : '' })}
                            minDate={query.from ? new Date(query.from) : null}
                            maxDate={new Date()}
                            value={query.to ? new Date(query.to) : null}
                            format="MM-dd-y hh:mm a"
                            calendarType={'US'}
                            disabled={isLoading}
                        />
                    </div>
                </div>
                <div
                    className="d-flex flex-row "
                    css={css`
                        gap: 12px;
                        flex-wrap: wrap;
                        justify-content: center;
                    `}
                >
                    {teamId ? null : (
                        <TeamSelectFilter
                            selectedTeamOption={query.team}
                            setSelectedTeamOption={(option) => handleFilterChange('team', option)}
                            disabled={isLoading}
                        />
                    )}
                    {channelId ? null : (
                        <div className="d-flex align-items-center">
                            {teamId && <span className="mr-2">Channel:</span>}
                            <ChannelSelectFilter
                                teamIds={[query.team?.value]}
                                selectedOption={query.room}
                                onRoomSelect={(option) => handleFilterChange('room', option)}
                                disabled={isLoading}
                                tbdRoom={true}
                            />
                        </div>
                    )}
                    {isAdminView && (
                        <div style={{ width: '200px' }}>
                            <StatusSelect value={query.status} onChange={handleStatusFilterChange} />
                        </div>
                    )}
                    <div style={{ width: '200px' }}>
                        <RecordingStatusSelect
                            value={query.recordingStatus}
                            onChange={handleRecordingStatusFilterChange}
                        />
                    </div>
                </div>
            </div>

            {/* Main Table */}
            <div
                className="custom-card card-table d-flex flex-column justify-content-between"
                css={css`
                    min-height: 675px;
                `}
            >
                <AdminTable className="w-100 text-dark-grey">
                    <thead>
                        <tr>
                            <th>SN</th>
                            <th className="text-left">Team</th>
                            <th className="text-left">Room</th>
                            <th
                                className="text-left"
                                onClick={() => handleSort('startedAt')}
                                style={{ cursor: 'pointer' }}
                            >
                                Started At
                                <SortedTableHeaderIcon sortConfig={sortConfig} fieldName="startedAt" />
                            </th>
                            <th className="text-left">Duration</th>
                            <th className="text-left">Status</th>
                            <th className="text-left">Participants</th>
                            <th className="text-center">Recording</th>
                        </tr>
                    </thead>
                    <tbody className="text-light-grey">
                        {isLoading ? (
                            <tr>
                                <td colSpan={7}>
                                    <LoadingSpiner size={42} height={200} />
                                </td>
                            </tr>
                        ) : items?.results?.length ? (
                            items.results.map((item, index) => {
                                return (
                                    <ConferenceRow
                                        key={item._id || item.id}
                                        item={item}
                                        index={index}
                                        query={query}
                                        onUpdateItem={onUpdateItem}
                                    />
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={7}>
                                    <div
                                        className="w-100 h-100 d-flex flex-row justify-content-center align-items-center"
                                        css={css`
                                            margin-top: 100px;
                                            width: 100%;
                                        `}
                                    >
                                        No Conferences
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </AdminTable>

                {items.totalResults > 0 && (
                    <div className="m-4">
                        <Pagination
                            pageNo={query.page}
                            totalRecords={items.totalResults}
                            pageLimit={10}
                            pageNeighbours={1}
                            onPageChanged={onPageChanged}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default connect()(Conference);
